import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SEOHead } from '@/components/seo/SEOHead';
import { Button } from '@/components/ui/button';

/**
 * The public data-deletion page. Meta App Review requires it, and a homeowner
 * who messaged a roofing company through Instagram or Messenger reaches it to
 * find out what is held and how to have it removed.
 *
 * The page is static. It fetches nothing and reads no credential, so it
 * renders identically whether or not a Meta app secret is configured. That is
 * a structural property, not a check: there is no data dependency to fail.
 *
 * Only semantic tokens, and no `dark:` utility: the marketing site has no
 * `.dark` class, it themes with `data-theme="marketing-dark"`, so a `dark:`
 * rule here would never fire. Its Tailwind config also defines no `brand`,
 * `info`, `success` or `warning` key, so `Button variant="brand"` would render
 * with no background.
 */

const LAST_UPDATED = 'October 10, 2026';
const SUPPORT_EMAIL = 'support@rooflyzer.pro';

const HOLDINGS = [
  'The messages you sent and the replies you received.',
  'The date and the time of each message.',
  'The channel you used: the website chat, SMS, Instagram or Messenger.',
  'The account identifier the channel gave us. On Instagram and Messenger this is an app-scoped identifier from Meta. It is not your name and it is not your email address.',
  'The name, the telephone number or the email address that you gave the roofing company yourself, when you gave one.',
  'A recording and a written transcript, when you spoke to the company on the telephone.',
];

const RELATED = [
  { label: 'Privacy policy', to: '/privacy' },
  { label: 'Terms of service', to: '/terms' },
  { label: 'Data freedom pledge', to: '/data-freedom' },
];

const LINK_CLASS =
  'text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

export const DataDeletionPage = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [showEmptyMessage, setShowEmptyMessage] = useState(false);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) {
      setShowEmptyMessage(true);
      return;
    }
    setShowEmptyMessage(false);
    navigate(`/data-deletion/status/${encodeURIComponent(trimmed)}`, {
      state: { code: trimmed },
    });
  };

  return (
    <>
      <SEOHead
        title="Data Deletion"
        description="How to ask Rooflyzer to delete the data it holds about you, what Rooflyzer keeps, and how long the deletion takes."
        canonicalUrl="/data-deletion"
        robots="index, follow"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Data Deletion', url: '/data-deletion' },
        ]}
      />

      <section className="pt-24 pb-24 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold text-foreground mb-2">Data deletion</h1>
          <p className="text-sm text-muted-foreground mb-8">Last updated: {LAST_UPDATED}</p>

          <p className="text-foreground/80">
            When you send a message to a roofing company through a Rooflyzer channel, Rooflyzer
            keeps a record of that conversation for the company. This page tells you what Rooflyzer
            keeps, how to ask for it to be deleted, and how long the deletion takes.
          </p>

          <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">
            What Rooflyzer holds about you
          </h2>
          <p className="text-foreground/80 mb-3">
            If you messaged a roofing company through one of these channels, Rooflyzer holds:
          </p>
          <ul className="text-muted-foreground space-y-1 list-disc pl-5">
            {HOLDINGS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p className="text-foreground/80 mt-3">
            Rooflyzer does not sell this data. The roofing company you messaged is the owner of it.
            Rooflyzer holds it for that company.
          </p>

          <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">
            How to ask for deletion
          </h2>
          <ol className="text-muted-foreground space-y-3 list-decimal pl-5">
            <li>
              <span className="font-medium text-foreground">Through Facebook or Instagram.</span>{' '}
              Open Settings and privacy, then Settings, then Apps and websites. Remove Rooflyzer,
              then press Send Request. Meta sends the request to Rooflyzer and gives you a
              confirmation code.
            </li>
            <li>
              <span className="font-medium text-foreground">By email.</span> Send an email to the
              address below. Write the channel you used and the name of the roofing company.
              Rooflyzer answers with a confirmation code.
              {/* The address is on its own line. A 44 px tap target needs
                  `min-h-11 inline-flex`, and that height inside running prose
                  breaks the sentence across uneven lines on a phone. */}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className={`${LINK_CLASS} min-h-11 inline-flex items-center mt-1`}
              >
                {SUPPORT_EMAIL}
              </a>
            </li>
          </ol>

          <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">How long it takes</h2>
          <p className="text-foreground/80">
            Rooflyzer records the request when it receives it. The deletion is complete
            within 30 days.
          </p>
          {/* Meta requires a written justification for anything that is not
              deleted. Do not cut this paragraph. */}
          <p className="text-foreground/80 mt-3">
            Some records cannot be deleted. United States law requires a business to keep a record
            of a transaction, of a signed document and of a telephone consent for a set period.
            Rooflyzer keeps those records for that period and no longer. Rooflyzer deletes
            everything else, and the status page tells you when the deletion is complete.
          </p>

          <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">
            Check the status of a request
          </h2>
          <p className="text-foreground/80 mb-3">Enter the confirmation code you received.</p>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1">
              <label
                htmlFor="deletion-code"
                className="text-sm font-medium text-foreground mb-1 block"
              >
                Confirmation code
              </label>
              <input
                id="deletion-code"
                name="code"
                type="text"
                inputMode="text"
                autoComplete="off"
                spellCheck={false}
                value={code}
                onChange={(event) => {
                  setCode(event.target.value);
                  if (showEmptyMessage) setShowEmptyMessage(false);
                }}
                aria-invalid={showEmptyMessage || undefined}
                aria-describedby={showEmptyMessage ? 'deletion-code-message' : undefined}
                className={`w-full min-h-11 rounded-md border bg-background px-3 text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  showEmptyMessage ? 'border-destructive' : 'border-input'
                }`}
              />
            </div>
            <Button type="submit" className="min-h-11 shrink-0">
              Check status
            </Button>
          </form>
          {showEmptyMessage && (
            <p id="deletion-code-message" role="status" className="text-sm text-foreground mt-2">
              {/* The glyph is decorative: the sentence carries the meaning, so
                  the message does not rest on colour. `destructive` in the
                  marketing-dark palette is a deep red that does not reach
                  4.5:1 on the page background. */}
              <span aria-hidden="true">⚠ </span>
              Enter the confirmation code first.
            </p>
          )}

          <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">Related pages</h2>
          <ul className="space-y-1">
            {RELATED.map((item) => (
              <li key={item.to}>
                <Link to={item.to} className={`${LINK_CLASS} min-h-11 inline-flex items-center`}>
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
};
